import { useEffect, useRef } from 'react';
import type { NetworkClockState } from '../types/clock';
import { log } from '../utils/logger';

/** Local tick interval in milliseconds.
 *
 *  This no longer drives how smoothly the clock reads: PlayerClock interpolates
 *  at frame rate via useSmoothClock, so the display stays fluid whatever this
 *  interval is. What still depends on it is everything derived from the store
 *  value — the cadence-aware low/critical thresholds (sound, vibration, ambient
 *  glow) in GameScreen, and, in local/AI games where there is no server, the
 *  time-expiry check in LocalGame's tickClock. 250ms keeps those responsive
 *  while re-rendering the game tree a quarter as often as the original 100ms. */
const LOCAL_TICK_INTERVAL = 250;

/**
 * Calls tickClock() every LOCAL_TICK_INTERVAL ms while the clock is running.
 * Uses a ref for tickClock so the interval isn't torn down and rebuilt on every
 * tick, but derives `shouldTick` from the prop: reading the ref during render
 * would see the previous value, since the sync effect only runs afterwards.
 *
 * Deliberately does NOT pause during a piece drag. It used to, to keep drags
 * fluid, but that also froze the store — and in local/AI games the store tick
 * is the only thing that detects a flag, so holding a piece down stopped the
 * clock from ever expiring.
 */
export function useClockTick(
  clockState: NetworkClockState | null,
  isPlaying: boolean,
  tickClock?: () => void
) {
  const tickIntervalRef = useRef<number | null>(null);
  const tickClockRef = useRef(tickClock);

  // Keep ref in sync with latest tickClock
  useEffect(() => {
    tickClockRef.current = tickClock;
  }, [tickClock]);

  // Derive a single stable boolean: should the clock be ticking?
  const shouldTick = !!clockState?.activePlayer && isPlaying && !!tickClock;

  useEffect(() => {
    // Fires once per shouldTick transition (dep [shouldTick]) — surfaces the
    // start/stop of the local clock interval.
    log.info('ClockTick', 'shouldTick changed', {
      shouldTick,
      hasActive: !!clockState?.activePlayer,
      isPlaying,
    });
    if (!shouldTick) {
      if (tickIntervalRef.current) {
        window.clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
        log.info('ClockTick', 'Interval cleared (shouldTick false)');
      }
      return;
    }

    tickIntervalRef.current = window.setInterval(() => {
      tickClockRef.current?.();
    }, LOCAL_TICK_INTERVAL);
    log.info('ClockTick', 'Interval started', { intervalMs: LOCAL_TICK_INTERVAL });

    return () => {
      if (tickIntervalRef.current) {
        window.clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
        log.info('ClockTick', 'Interval cleared (cleanup)');
      }
    };
  }, [shouldTick]);
}
